import clsx from 'clsx';
import { ComponentProps, FC } from 'react';

import { Chevron } from 'src/general/components/Chevron/Chevron';
import { ConditionalWrapper } from 'src/general/components/ConditionalWrapper/ConditionalWrapper';
import {
  TooltipContentDynamic,
  TooltipDynamic,
  TooltipTriggerDynamic,
} from 'src/general/components/Tooltip/TooltipDynamic';
import {
  FilterHierarchyFieldsGroupOption,
  FilterOption,
  PlpFiltersConditionalProps,
} from 'src/plp/components/PlpFilters/PlpFilters.type';
import { pluralise } from 'src/utils/pluralise';

import styles from './FilterHierarchyFieldsGroup.module.scss';
import { FilterOptionField } from './FilterOptionField';
import { FilterOptionFieldsGroup } from './FilterOptionFieldsGroup';

type FilterHierarchyFieldProps = {
  option: FilterHierarchyFieldsGroupOption;
  currentFilters: FilterOption[];
  subCategoryLabel: string;
} & PlpFiltersConditionalProps;

export const FilterHierarchyField: FC<FilterHierarchyFieldProps> = ({
  option,
  currentFilters,
  subCategoryLabel,
  ...props
}) => {
  const hasChildren = !!option.children?.length;
  const parentOptionSelected = currentFilters.some((filter) => filter.value === option.value);
  const selectedSubCategoryOptions = currentFilters.filter((filter) =>
    option.children?.some((child) => child.value === filter.value),
  );
  const indeterminate = !parentOptionSelected && !!selectedSubCategoryOptions.length;
  let optionLabelCount: string | null = null;
  if (parentOptionSelected && hasChildren) {
    optionLabelCount = `All ${pluralise(subCategoryLabel, 2, { skipNumber: true }).toLowerCase()} selected`;
  } else if (indeterminate) {
    optionLabelCount = `${pluralise(subCategoryLabel, selectedSubCategoryOptions.length).toLowerCase()} selected`;
  }
  return (
    <ConditionalWrapper condition={hasChildren} wrapper={tooltipWrapper}>
      <div className={clsx(styles.parentOption, 'group/chevron')}>
        <ConditionalWrapper condition={hasChildren} wrapper={tooltipTriggerWrapper}>
          <FilterOptionField
            {...option}
            indeterminate={indeterminate}
            label={
              optionLabelCount ? (
                <>
                  {option.label}
                  <small>{optionLabelCount}</small>
                </>
              ) : (
                option.label
              )
            }
            checked={parentOptionSelected}
            {...props}
          />
        </ConditionalWrapper>
        {hasChildren && (
          <>
            <span className="absolute top-2 right-2">
              <Chevron />
            </span>
            {props.type === 'modal' ? (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  props.onSelectHierarchy?.({
                    option,
                    filterTitle: ['make', 'model'].includes(option.filter)
                      ? `${option.label}`
                      : `${option.labelNoCount} ${pluralise(subCategoryLabel, 2, { skipNumber: true })}`,
                  });
                }}
                className={styles.modalHierarchyTrigger}
                data-testid={`filter-expand-make-${option.slug.toLowerCase()}`}
              >
                <span className="sr-only">
                  Show {option.labelNoCount} {pluralise(subCategoryLabel, 2, { skipNumber: true })}
                </span>
              </button>
            ) : (
              <TooltipContentDynamic className={styles.tooltipContent}>
                <h3 className={styles.tooltipTitle}>
                  {option.labelNoCount} {pluralise(subCategoryLabel, 2, { skipNumber: true })}
                </h3>
                <FilterOptionFieldsGroup options={option.children!} currentFilters={currentFilters} {...props} />
              </TooltipContentDynamic>
            )}
          </>
        )}
      </div>
    </ConditionalWrapper>
  );
};

const tooltipWrapper: ComponentProps<typeof ConditionalWrapper>['wrapper'] = (children) => (
  <TooltipDynamic placement="right-start" fallbackPlacements={['right-start', 'right', 'right-end']}>
    {children}
  </TooltipDynamic>
);

const tooltipTriggerWrapper: ComponentProps<typeof ConditionalWrapper>['wrapper'] = (children) => (
  <TooltipTriggerDynamic>{children}</TooltipTriggerDynamic>
);
